import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Languages, LayoutGrid, LogOut } from 'lucide-react';
import { InstallApp } from '@/components/InstallApp';
import { SupportAvailabilityToggle } from '@/components/layout/SupportAvailabilityToggle';
import { BottomSheet, BottomTabBar, SettingsGroup, SettingsRow, type BottomTab } from '@/components/mobile';
import { defineAbilitiesFor } from '@/lib/abilities';
import { useAuthStore } from '@/store/authStore';
import { SUPPORTED_LANGUAGES, normalizeLanguage, supportedLanguageCodes } from '@/i18n/languageConfig';
import { HealthPill } from './TopBar';
import { buildPhoneMoreGroups, isPathActive, visiblePhoneTabs } from './phoneNavModel';
import { useLogout } from './useLogout';

const MORE_KEY = '__more';

/**
 * Phone (<md) primary navigation: floating bottom tab bar + More sheet.
 * Tabs and More rows use the same CASL abilities as the md+ sidebar.
 */
export function PhoneNav(): JSX.Element {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const role = useAuthStore((s) => s.user?.role);
    const user = useAuthStore((s) => s.user);
    const ability = useMemo(() => defineAbilitiesFor(role), [role]);
    const tabs = useMemo(() => visiblePhoneTabs(ability), [ability]);
    const moreGroups = useMemo(() => buildPhoneMoreGroups(ability), [ability]);
    const logout = useLogout();
    const [moreOpen, setMoreOpen] = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const current = normalizeLanguage(i18n.language);

    useEffect(() => {
        setMoreOpen(false);
    }, [location.pathname]);

    const onTab = tabs.some((tab) => isPathActive(location.pathname, tab.to, tab.exact));
    const barTabs: BottomTab[] = [
        ...tabs.map((tab) => ({
            key: tab.key,
            label: t(tab.i18nKey, tab.fallback),
            to: tab.to,
            icon: tab.icon,
            active: !moreOpen && isPathActive(location.pathname, tab.to, tab.exact),
        })),
        {
            key: 'more',
            label: t('mobile.tabs.more', 'More'),
            to: MORE_KEY,
            icon: LayoutGrid,
            active: moreOpen || !onTab,
            onSelect: () => setMoreOpen(true),
            expanded: moreOpen,
        },
    ];

    const name = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email : '';

    return (
        <>
            <BottomTabBar tabs={barTabs} label={t('mobile.tabs.label', 'Main navigation')} />
            <BottomSheet open={moreOpen} onOpenChange={setMoreOpen} title={t('mobile.tabs.more', 'More')}>
                <div className="space-y-4 pb-2">
                    {name ? (
                        <div className="rounded-[18px] bg-[var(--m-canvas)] px-4 py-3">
                            <p className="truncate text-[15px] font-semibold text-[#0F2A44]">{name}</p>
                            {user?.role ? <p className="truncate text-[13px] text-[#64748B]">{user.role}</p> : null}
                        </div>
                    ) : null}
                    {moreGroups.map((group) => (
                        <SettingsGroup key={group.i18nKey} title={t(group.i18nKey, group.label)}>
                            {group.rows.map((row) => (
                                <SettingsRow key={row.to} to={row.to} icon={row.icon} label={t(row.i18nKey, row.i18nKey)} testId={`pa-more-${row.to}`} />
                            ))}
                        </SettingsGroup>
                    ))}
                    <SettingsGroup title={t('mobile.more.status', 'Status')}>
                        <div className="flex items-start gap-2 px-4 py-3">
                            <div className="min-w-0 flex-1">
                                <HealthPill inline />
                            </div>
                            <SupportAvailabilityToggle />
                        </div>
                    </SettingsGroup>
                    <SettingsGroup title={t('mobile.more.app', 'App')}>
                        <SettingsRow
                            icon={Languages}
                            label={t('nav.selectLanguage', 'Select language')}
                            value={SUPPORTED_LANGUAGES[current].nativeLabel}
                            onClick={() => setLanguageOpen(true)}
                            testId="pa-more-language"
                        />
                        <div className="px-4">
                            <InstallApp />
                        </div>
                        <SettingsRow icon={LogOut} iconFlipRtl destructive label={t('nav.logout', 'Log out')} onClick={() => void logout()} testId="pa-more-logout" />
                    </SettingsGroup>
                </div>
            </BottomSheet>
            <BottomSheet open={languageOpen} onOpenChange={setLanguageOpen} title={t('nav.selectLanguage', 'Select language')}>
                <div role="radiogroup" aria-label={t('nav.selectLanguage', 'Select language')} className="divide-y divide-[#0F2A44]/[0.06] overflow-hidden rounded-[18px] bg-white">
                    {supportedLanguageCodes.map((code) => {
                        const active = code === current;
                        return (
                            <button
                                key={code}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => {
                                    setLanguageOpen(false);
                                    if (!active) void i18n.changeLanguage(code);
                                }}
                                className="flex min-h-12 w-full items-center justify-between gap-2 px-4 text-start text-[15px] font-medium text-[#0F2A44] hover:bg-[#0F2A44]/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1FB7A6]"
                            >
                                <span className="truncate">{SUPPORTED_LANGUAGES[code].nativeLabel}</span>
                                {active ? <Check className="h-5 w-5 shrink-0 text-[#146F66]" aria-hidden="true" /> : null}
                            </button>
                        );
                    })}
                </div>
            </BottomSheet>
        </>
    );
}
